import {Button, Card, Choice, DataStatus, Flag, Switch, TextInput} from 'platform/components';
import {Box, Heading, HStack, Icon, Right, Text, ThemeIconKey, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../i18n/i18n';
import {useImportMarket} from '../hooks/useImportMarket';
import {ImportUploadBox} from './ImportUploadBox';

export type ImportFormPayload = {
  file: File;
  market: string;
  name?: string;
  use_vin_decoding?: boolean;
};

interface ImportUploadFormProps extends TestIdProps {
  onImport: (payload: ImportFormPayload) => void;
  isImporting?: boolean;
}

export function ImportUploadForm(props: ImportUploadFormProps) {
  const {marketOptions, defaultMarket, isLoading} = useImportMarket();
  const [market, setMarket] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [useVinDecoding, setUseVinDecoding] = useState(false);

  const effectiveMarket = market ?? defaultMarket;

  const handleFileSelect = (file: File | null) => {
    setSelectedFile(file);
    if (isNotNil(file)) {
      setName(fileNameWithoutExtension(file.name));
    }
  };

  const handleSubmit = () => {
    if (isNil(selectedFile)) {
      return;
    }
    props.onImport({
      file: selectedFile,
      market: effectiveMarket,
      ...(name.trim() !== '' && {name: name.trim()}),
      use_vin_decoding: useVinDecoding,
    });
  };

  return (
    <Box maxWidth={150} width="100%">
      <Card data-testid={suffixTestId('card', props)}>
        <DataStatus
          isLoading={isLoading}
          data-testid={suffixTestId('languageLoadingStatus', props)}
        >
          <Box padding={6}>
            <VStack spacing={6}>
              <Heading size={2}>{i18n.t('page.import.labels.title')}</Heading>
              <Text size="small" color="secondary">
                {i18n.t('page.import.labels.subtitle')}
              </Text>
              <TextInput
                label={i18n.t('page.import.labels.importName')}
                value={name}
                onChange={(value) => setName(value ?? '')}
                helperText={i18n.t('page.import.labels.importNameHelper')}
                data-testid={suffixTestId('importNameInput', props)}
              />
              <Choice
                label={i18n.t('page.import.labels.market')}
                value={effectiveMarket}
                options={marketOptions}
                onChange={(value) => setMarket(value ?? defaultMarket)}
                formatOptionLabel={(option) => (
                  <HStack spacing={2} align="center">
                    <Icon
                      value={`flags/${option.value}` as ThemeIconKey}
                      size={5}
                      color="palettes.neutral.800.100"
                    />
                    <Text size="small">{option.label ?? option.value}</Text>
                  </HStack>
                )}
                isNotClearable
                menuInPortal
                helperText={i18n.t('page.import.labels.marketHelper')}
                data-testid={suffixTestId('marketChoice', props)}
              />
              <ImportUploadBox
                selectedFile={selectedFile}
                onFileSelect={handleFileSelect}
                data-testid={suffixTestId('fileDropZone', props)}
              />
              <Box
                backgroundColor={useVinDecoding ? 'palettes.blue.10.100' : 'palettes.white.10.100'}
                border="1px solid"
                borderLeft={useVinDecoding ? '3px solid' : undefined}
                borderColor={useVinDecoding ? 'palettes.blue.30.100' : 'palettes.neutral.80.100'}
                padding={4}
                borderRadius="small"
                role="region"
                aria-label={i18n.t('page.import.labels.useVinDecoder')}
                data-testid={suffixTestId('vinDecoderAlert', props)}
              >
                <VStack spacing={2}>
                  <HStack spacing={2} align="center">
                    <Switch
                      value={useVinDecoding}
                      onChange={setUseVinDecoding}
                      label={i18n.t('page.import.labels.useVinDecoder')}
                      data-testid={suffixTestId('useVinDecoderSwitch', props)}
                    />
                    <Flag
                      size="small"
                      label={i18n.t('page.import.labels.paidFeature')}
                      colorScheme="purple"
                      data-testid={suffixTestId('paidFeatureFlag', props)}
                    />
                  </HStack>
                  <Box paddingLeft={12}>
                    <Text size="xSmall" color="secondary">
                      {i18n.t('page.import.labels.useVinDecoderDescription')}
                    </Text>
                  </Box>
                </VStack>
              </Box>
              <Right>
                <Button
                  title={i18n.t('page.import.actions.importFile')}
                  isDisabled={isNil(selectedFile)}
                  isLoading={props.isImporting}
                  onClick={handleSubmit}
                  data-testid={suffixTestId('importButton', props)}
                />
              </Right>
            </VStack>
          </Box>
        </DataStatus>
      </Card>
    </Box>
  );
}

function fileNameWithoutExtension(fileName: string): string {
  return fileName.replace(/\.[^/.]+$/, '');
}
